import { createClient, SupabaseClient } from '@supabase/supabase-js'
import type { User } from '@supabase/supabase-js'

let serviceClient: SupabaseClient | null = null

/**
 * Get or create a Supabase service client for onboarding operations
 */
function getServiceClient(): SupabaseClient {
  if (serviceClient) {
    return serviceClient
  }

  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
  const serviceRoleKey = process.env.SUPABASE_SERVICE_ROLE_KEY

  if (!supabaseUrl || !serviceRoleKey) {
    throw new Error(
      'Missing required environment variables: NEXT_PUBLIC_SUPABASE_URL and SUPABASE_SERVICE_ROLE_KEY'
    )
  }

  serviceClient = createClient(supabaseUrl, serviceRoleKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false
    }
  })

  return serviceClient
}

export interface TherapistOnboardingData {
  // Auth info (required)
  email: string
  password: string
  
  // User metadata for auth
  metadata?: Record<string, any>
  
  // Profile data - any additional fields will be inserted into the therapists table
  profileData: Record<string, any>
}

export interface OnboardingResult {
  success: boolean
  user?: User
  therapistId?: string
  error?: string
}

/**
 * Onboarding service for creating new therapists
 */
export const onboardingService = {
  /**
   * Create a new therapist with auth account and profile
   */
  async createTherapist(data: TherapistOnboardingData): Promise<OnboardingResult> {
    const client = getServiceClient()
    
    try {
      // Step 1: Create auth user
      const { data: authData, error: authError } = await client.auth.admin.createUser({
        email: data.email,
        password: data.password,
        email_confirm: true,
        user_metadata: data.metadata || {}
      })

      if (authError) {
        return {
          success: false,
          error: `Failed to create auth user: ${authError.message}`
        }
      }

      const user = authData.user

      // Step 2: Create therapist profile with the provided data
      // Ensure id and authId are both set to the auth user's ID
      const profilePayload = {
        ...data.profileData,
        id: user.id,
        authId: user.id,
        email: data.email, // Ensure email matches auth
        createdAt: data.profileData.createdAt || new Date().toISOString(),
        updatedAt: data.profileData.updatedAt || new Date().toISOString()
      }

      const { data: therapistData, error: profileError } = await client
        .from('pros')
        .insert(profilePayload)
        .select()
        .single()

      if (profileError) {
        // Rollback: delete the auth user if profile creation fails
        await client.auth.admin.deleteUser(user.id)
        
        return {
          success: false,
          error: `Failed to create therapist profile: ${profileError.message}`
        }
      }

      return {
        success: true,
        user,
        therapistId: therapistData.id
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      }
    }
  },

  /**
   * Bulk create multiple therapists
   */
  async createTherapistsBulk(
    therapists: TherapistOnboardingData[]
  ): Promise<Map<string, OnboardingResult>> {
    const results = new Map<string, OnboardingResult>()

    for (const therapist of therapists) {
      const result = await this.createTherapist(therapist)
      results.set(therapist.email, result)
      
      // Add delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 1000))
    }

    return results
  },

  /**
   * Update therapist onboarding status
   */
  async updateOnboardingStatus(
    therapistId: string,
    status: {
      onboardingCompleted?: boolean
      documentsSubmitted?: boolean
      profileCompleted?: boolean
      availabilitySet?: boolean
    }
  ): Promise<boolean> {
    const client = getServiceClient()
    
    const { error } = await client
      .from('pros')
      .update({
        onboarding_completed: status.onboardingCompleted,
        documents_submitted: status.documentsSubmitted,
        profile_completed: status.profileCompleted,
        availability_set: status.availabilitySet,
        updatedAt: new Date().toISOString()
      })
      .eq('id', therapistId)

    return !error
  },

  /**
   * Get therapist by email (including auth user data)
   */
  async getTherapistByEmail(email: string): Promise<{
    user?: User
    profile?: any
    error?: string
  }> {
    const client = getServiceClient()

    // Get all auth users and filter by email
    const { data: { users }, error: authError } = await client.auth.admin.listUsers({
      page: 1,
      perPage: 1000 // Increase limit to get all users
    })

    if (authError) {
      return { error: authError.message }
    }

    const user = users.find(u => u.email === email)
    if (!user) {
      return { error: 'User not found' }
    }

    // Get therapist profile
    const { data: profile, error: profileError } = await client
      .from('pros')
      .select('*')
      .eq('id', user.id)
      .single()

    if (profileError) {
      return { user, error: `Profile not found: ${profileError.message}` }
    }

    return { user, profile }
  },

  /**
   * Delete therapist (both auth and profile)
   */
  async deleteTherapist(therapistId: string): Promise<{
    success: boolean
    error?: string
  }> {
    const client = getServiceClient()

    try {
      // Delete profile first
      const { error: profileError } = await client
        .from('pros')
        .delete()
        .eq('id', therapistId)

      if (profileError) {
        return {
          success: false,
          error: `Failed to delete profile: ${profileError.message}`
        }
      }

      // Then delete auth user
      const { error: authError } = await client.auth.admin.deleteUser(therapistId)

      if (authError) {
        return {
          success: false,
          error: `Failed to delete auth user: ${authError.message}`
        }
      }

      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  },

  /**
   * Delete therapist by email (both auth and profile)
   */
  async deleteTherapistByEmail(email: string): Promise<{
    success: boolean
    deletedUser?: string
    error?: string
  }> {
    const client = getServiceClient()

    try {
      // First, find the user by email
      const { data: authData, error: listError } = await client.auth.admin.listUsers()
      
      if (listError) {
        return {
          success: false,
          error: `Failed to list users: ${listError.message}`
        }
      }

      const user = authData.users.find(u => u.email === email)
      
      if (!user) {
        return {
          success: false,
          error: `No user found with email: ${email}`
        }
      }

      // Delete profile first
      const { error: profileError } = await client
        .from('pros')
        .delete()
        .eq('id', user.id)

      if (profileError) {
        // Profile might not exist, but we still want to delete the auth user
        console.warn(`Profile deletion warning: ${profileError.message}`)
      }

      // Then delete auth user
      const { error: authError } = await client.auth.admin.deleteUser(user.id)

      if (authError) {
        return {
          success: false,
          error: `Failed to delete auth user: ${authError.message}`
        }
      }

      return { 
        success: true,
        deletedUser: email
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }
}